import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { PageBreadcrumbs } from '@/ui/layout/PageBreadcrumbs';
import { layoutModeTokens, useLayoutMode } from '@/ui/layouts/layoutMode';
import { MenuIcon, NotificationsIcon, PersonIcon } from '@/ui/primitives';
import { s } from '../../theme/tokens';
import {
  Box,
  Drawer,
  IconButton,
  ListSubheader,
  MenuItem,
  MenuList,
  Text,
  useTheme,
} from '../primitives';
import { Popover } from '../primitives/Popover';
import { Stack } from './Stack';
import type { PropsWithChildren, ReactElement, ReactNode } from 'react';

type AppShellProps = PropsWithChildren<{
  title?: ReactNode;
  nav?: ReactNode;
  actions?: ReactNode;
  sidebar?: ReactNode;
  showDefaultActions?: boolean;
}>;

export const AppShell = ({
  title,
  nav,
  actions,
  sidebar,
  showDefaultActions = true,
  children,
}: AppShellProps): ReactElement => {
  const theme = useTheme();
  const layoutMode = useLayoutMode();
  const { items: breadcrumbItems, visible: breadcrumbsVisible } = useBreadcrumbs();
  const showBreadcrumbs = breadcrumbsVisible && breadcrumbItems.length > 0;
  const isMobile = layoutMode !== layoutModeTokens.wide;
  const headerPaddingX = isMobile ? s('sm') : s('md');
  const [open, setOpen] = useState(false);
  const [notificationsAnchor, setNotificationsAnchor] = useState<HTMLElement | null>(null);
  const [userAnchor, setUserAnchor] = useState<HTMLElement | null>(null);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const hasSidebar = Boolean(sidebar);
  const hasHeader = [title, nav, actions, hasSidebar].some(Boolean);
  const useGridHeaderLayout = isMobile && hasSidebar;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const offset = hasSidebar && !isMobile ? `${theme.app.sizes.sidebarWidth}px` : '0px';
    document.body.style.setProperty('--app-dialog-offset', offset);
    return () => {
      document.body.style.removeProperty('--app-dialog-offset');
    };
  }, [hasSidebar, isMobile, theme.app.sizes.sidebarWidth]);

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const height = headerRef.current?.offsetHeight ?? 0;
    document.body.style.setProperty('--app-topbar-height', `${height}px`);
    return () => {
      document.body.style.removeProperty('--app-topbar-height');
    };
  }, [hasHeader, headerPaddingX]);
  return (
    <Box
      sx={{
        height: '100dvh',
        minHeight: '100vh',
        backgroundColor: 'background.default',
        display: 'grid',
        gridTemplateRows: hasHeader ? 'auto 1fr' : '1fr',
        overflow: 'hidden',
      }}
    >
      <a className="flow-skip-link" href="#main-content">
        Skip to main content
      </a>
      {hasHeader && (
        <Box
          component="header"
          ref={headerRef}
          sx={(currentTheme) => ({
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: currentTheme.app.colors.surface,
            borderBottom: `1px solid ${currentTheme.app.colors.border}`,
            paddingTop: s('none'),
            paddingBottom: s('xs'),
            minWidth: 0,
          })}
        >
          <Box
            sx={{
              display: useGridHeaderLayout ? 'grid' : 'flex',
              gridTemplateColumns: useGridHeaderLayout ? 'auto 1fr auto' : undefined,
              alignItems: 'center',
              gap: s('md'),
              flexWrap: 'nowrap',
              minWidth: 0,
              width: '100%',
              px: headerPaddingX,
            }}
          >
            {hasSidebar && isMobile ? (
              <IconButton
                aria-label="Open navigation"
                onClick={() => {
                  setOpen(true);
                }}
                sx={{
                  '&:hover': { backgroundColor: 'transparent' },
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: s('sm'),
                  marginTop: (muiTheme) => muiTheme.spacing(0),
                  padding: (muiTheme) => muiTheme.spacing(0.25),
                  width: (muiTheme) => muiTheme.app.iconSizes.xxl,
                  height: (muiTheme) => muiTheme.app.iconSizes.xxl,
                  lineHeight: 0,
                  '& .MuiBox-root': {
                    width: (muiTheme) => muiTheme.app.iconSizes.xxl,
                    height: (muiTheme) => muiTheme.app.iconSizes.xxl,
                  },
                  '& .MuiBox-root svg': {
                    width: '100%',
                    height: '100%',
                  },
                  '& svg': {
                    width: (muiTheme) => muiTheme.app.iconSizes.xxl,
                    height: (muiTheme) => muiTheme.app.iconSizes.xxl,
                    display: 'block',
                  },
                }}
              >
                <MenuIcon fontSize="inherit" />
              </IconButton>
            ) : null}
            {title ? (
              <Box
                sx={{
                  justifySelf: useGridHeaderLayout ? 'center' : 'start',
                  textAlign: useGridHeaderLayout ? 'center' : 'left',
                }}
              >
                <Text role="sectionTitle">{title}</Text>
              </Box>
            ) : null}
            {nav && !useGridHeaderLayout ? (
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>{nav}</Box>
            ) : !useGridHeaderLayout ? (
              <Box sx={{ flexGrow: 1, minWidth: 0 }} />
            ) : null}
            <Stack direction="row" gap="sm" sx={{ justifySelf: 'end' }}>
              {showDefaultActions ? (
                <>
                  <IconButton
                    size="large"
                    aria-label="Notifications"
                    onClick={(event) => {
                      setNotificationsAnchor(event.currentTarget);
                    }}
                    sx={{
                      '&:hover': { backgroundColor: 'transparent' },
                      '& svg': {
                        width: (muiTheme) => muiTheme.app.iconSizes.lg,
                        height: (muiTheme) => muiTheme.app.iconSizes.lg,
                      },
                    }}
                  >
                    <NotificationsIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="large"
                    aria-label="Account"
                    onClick={(event) => {
                      setUserAnchor(event.currentTarget);
                    }}
                    sx={{
                      '&:hover': { backgroundColor: 'transparent' },
                      '& svg': {
                        width: (muiTheme) => muiTheme.app.iconSizes.lg,
                        height: (muiTheme) => muiTheme.app.iconSizes.lg,
                      },
                    }}
                  >
                    <PersonIcon fontSize="inherit" />
                  </IconButton>
                </>
              ) : null}
              {actions ? <Box>{actions}</Box> : null}
            </Stack>
          </Box>
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: hasSidebar ? (isMobile ? '1fr' : 'auto 1fr') : '1fr',
          minHeight: 0,
          height: '100%',
        }}
      >
        {hasSidebar ? (
          <>
            <Box
              component="aside"
              sx={(currentTheme) => {
                const sidebarOffset = 'var(--app-topbar-height, 54px)';
                return {
                  display: isMobile ? 'none' : 'block',
                  width: currentTheme.app.sizes.sidebarWidth,
                  borderRight: `1px solid ${currentTheme.app.colors.border}`,
                  backgroundColor: currentTheme.app.colors.surface,
                  paddingX: s('md'),
                  paddingY: s('lg'),
                  position: 'sticky',
                  top: sidebarOffset,
                  alignSelf: 'start',
                  height: `calc(100vh - ${sidebarOffset})`,
                  overflow: 'auto',
                };
              }}
            >
              {sidebar}
            </Box>
            <Drawer
              open={open}
              onClose={() => {
                setOpen(false);
              }}
              sx={{ display: isMobile ? 'block' : 'none' }}
              slotProps={{
                paper: {
                  sx: {
                    width: theme.app.sizes.sidebarWidth,
                    paddingX: s('md'),
                    paddingY: s('lg'),
                    backgroundColor: theme.app.colors.surface,
                    backgroundImage: 'none',
                    height: '100%',
                  },
                },
              }}
            >
              <Box
                sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
                onClickCapture={(event) => {
                  const target = event.target as HTMLElement | null;
                  if (!target) return;
                  if (target.closest('a[data-has-popover="true"]')) return;
                  if (target.closest('a[href]')) {
                    setOpen(false);
                  }
                }}
              >
                {sidebar}
              </Box>
            </Drawer>
          </>
        ) : null}
        <Box
          component="main"
          id="main-content"
          sx={{
            flex: 1,
            minWidth: 0,
            px: isMobile && hasSidebar ? s('sm') : s('none'),
            minHeight: 0,
            overflowY: 'auto',
            scrollbarGutter: 'stable both-edges',
            overscrollBehavior: 'contain',
          }}
        >
          {showBreadcrumbs ? (
            <Box
              sx={(currentTheme) => ({
                borderBottom: `1px solid ${currentTheme.app.colors.border}`,
                paddingX: s('md'),
                paddingTop: s('md'),
                paddingBottom: s('md'),
              })}
            >
              <PageBreadcrumbs />
            </Box>
          ) : null}
          {children}
        </Box>
      </Box>
      {showDefaultActions ? (
        <>
          <Popover
            open={Boolean(notificationsAnchor)}
            anchorEl={notificationsAnchor}
            onClose={() => {
              setNotificationsAnchor(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            minWidth={240}
          >
            <MenuList
              dense
              disablePadding
              subheader={
                <ListSubheader
                  disableSticky
                  sx={{
                    typography: 'label',
                    paddingX: s('sm'),
                    paddingY: s('xs'),
                    lineHeight: 1.4,
                    backgroundColor: 'transparent',
                  }}
                >
                  Notifications
                </ListSubheader>
              }
            >
              <MenuItem
                sx={{
                  minHeight: s('2xl'),
                  paddingY: s('xs'),
                  typography: 'body',
                  fontSize: (muiTheme) => muiTheme.typography.caption.fontSize,
                  lineHeight: (muiTheme) => muiTheme.typography.caption.lineHeight,
                  fontWeight: (muiTheme) => muiTheme.typography.caption.fontWeight,
                  color: 'text.secondary',
                }}
              >
                No new alerts.
              </MenuItem>
            </MenuList>
          </Popover>
          <Popover
            open={Boolean(userAnchor)}
            anchorEl={userAnchor}
            onClose={() => {
              setUserAnchor(null);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            minWidth={200}
          >
            <MenuList
              dense
              disablePadding
              subheader={
                <ListSubheader
                  disableSticky
                  sx={{
                    typography: 'label',
                    paddingX: s('sm'),
                    paddingY: s('xs'),
                    lineHeight: 1.4,
                    backgroundColor: 'transparent',
                  }}
                >
                  Account
                </ListSubheader>
              }
            >
              <MenuItem
                sx={{
                  minHeight: s('2xl'),
                  paddingY: s('xs'),
                  typography: 'body',
                  fontSize: (muiTheme) => muiTheme.typography.caption.fontSize,
                  lineHeight: (muiTheme) => muiTheme.typography.caption.lineHeight,
                  fontWeight: (muiTheme) => muiTheme.typography.caption.fontWeight,
                  color: 'text.secondary',
                }}
              >
                Signed in as demo@flowqa.dev
              </MenuItem>
            </MenuList>
          </Popover>
        </>
      ) : null}
    </Box>
  );
};
